import React from 'react';

/**
 * EtableringRow - Specialrad för "Etablering, avfall & admin" i byggoffert
 * 
 * Denna rad har egen kommentarslogik och visas alltid i byggoffert-kategorin.
 * Kommentarslogiken är liknande EstimatItemRowCustomer men med vissa skillnader.
 */
const EtableringRow = ({
  categoryKey,
  etableringAdminTotal,
  comments,
  commentsRaw,
  estimatData,
  showComments,
  toggleCommentField,
  handleCommentChange,
  handleSaveComment,
  handleDeleteComment,
  handleSaveByggkonsultComment,
  isCustomerView = true,
  hasUnsavedChanges,
  isSavedLocally,
  logActivity,
}) => {
  const eaItemName = 'etablering_admin';
  const eaCommentKey = `${categoryKey}_${eaItemName}`;
  
  // Kontrollera om det finns kommentarer
  const hasCustomerText = (() => {
    const v = comments[eaCommentKey];
    const t = typeof v === 'object' ? (v?.text || '') : (v || '');
    return t.trim();
  })();
  
  const hasConsultantReply = Boolean(estimatData?.byggkonsultResponses?.[eaCommentKey]);

  return (
    <>
      {/* Etablering rad */}
      <tr className="border-t border-gray-200">
        <td className="px-2 sm:px-4 py-2 sm:py-3 text-sm text-gray-700">Etablering, avfall & admin</td>
        <td className="px-2 sm:px-4 py-2 sm:py-3 text-right text-sm text-gray-700">
          {etableringAdminTotal > 0 ? `${etableringAdminTotal.toLocaleString('sv-SE')} kr` : '-'}
        </td>
        <td className="p-0 sm:px-4 sm:py-3 text-center text-sm w-0 max-w-0 sm:max-w-none sm:w-auto overflow-hidden">
          {(() => {
            return (
              <div className="hidden sm:flex items-center justify-center gap-1">
                <span className="relative inline-block">
                  <button
                    onClick={() => toggleCommentField(categoryKey, eaItemName)}
                    className="text-blue-600 hover:text-blue-800 text-sm underline peer"
                    aria-label={(() => {
                      const v = commentsRaw[eaCommentKey];
                      const name = typeof v === 'object' ? v?.author?.name : null;
                      if (name) return `Kommentar från ${name}`;
                      return isCustomerView ? 'Kommentera' : 'Anteckna';
                    })()}
                  >
                    {(() => {
                      if (showComments[eaCommentKey]) return 'Dölj';
                      // För byggkonsult: skilja på "Visa anteckning" (egen) vs "Visa kommentar" (kund)
                      if (!isCustomerView) {
                        if (hasCustomerText) return 'Visa kommentar';
                        if (hasConsultantReply) return 'Visa anteckning';
                        return 'Anteckna';
                      }
                      // För kund-vy: Visa ENDAST om kunden själv har kommenterat
                      // Proaktiva byggkonsult-anteckningar är interna
                      if (!hasCustomerText) return 'Kommentera';
                      if (hasConsultantReply) return 'Visa svar';
                      return 'Visa kommentar';
                    })()}
                  </button>
                  {(() => {
                    const v = commentsRaw[eaCommentKey];
                    const name = typeof v === 'object' ? v?.author?.name : null;
                    return name;
                  })() && (
                    <span className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap opacity-0 peer-hover:opacity-100 transition-opacity">
                      Kommentar från {(() => {
                        const v = commentsRaw[eaCommentKey];
                        return typeof v === 'object' ? (v?.author?.name || '') : '';
                      })()}
                    </span>
                  )}
                </span>
                {(() => {
                  const v = commentsRaw[eaCommentKey];
                  const t = typeof v === 'object' ? (v?.text || '') : (v || '');
                  return t.trim();
                })() && (
                  <div className="w-2 h-2 bg-leadme-pink rounded-full flex-shrink-0 animate-pulse" title="Har kommentar"></div>
                )}
              </div>
            );
          })()}
        </td>
      </tr>
      
      {/* Kommentarsfält (expanderat) */}
      {showComments[eaCommentKey] && (
        <tr className="bg-blue-50">
          <td colSpan="3" className="px-2 sm:px-4 py-2 sm:py-3">
            {(() => {
              // Kontrollera om det finns befintlig kommentar eller byggkonsultsvar
              const hasCustomerComment = Boolean(commentsRaw[eaCommentKey]);
              const hasConsultantReply = Boolean(estimatData?.byggkonsultResponses?.[eaCommentKey]);
              // För kund-vy: visa bara innehåll om kunden har kommenterat
              // Byggkonsultens proaktiva anteckningar är interna
              const hasAnyContent = isCustomerView
                ? hasCustomerComment
                : (hasCustomerComment || hasConsultantReply);

              if (hasAnyContent) {
                // LÄSLÄGE: Visa KOMPLETTA tråden - EXAKT som feedback-sektionen
                return (
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-gray-700">Konversation om: Etablering, avfall & admin</h4>
                    
                    {(() => {
                      // Bygg KOMPLETTA tråden precis som feedback-sektionen
                      const val = commentsRaw[eaCommentKey];
                      const isDeleted = val && typeof val === 'object' && val.deleted;
                      const customerText = typeof val === 'object' ? (val?.text || '') : (val || '');
                      const customerAt = (typeof val === 'object' && (val?.commentedAt || val?.at)) ? (val.commentedAt || val.at) : null;
                      const customerName = (typeof val === 'object' && val?.author?.name) ? val.author.name : 'Du';

                      const consultantObj = estimatData?.byggkonsultResponses?.[eaCommentKey];
                      const consText = consultantObj ? (typeof consultantObj === 'object' ? (consultantObj?.text || '') : (consultantObj || '')) : '';
                      const consAt = (consultantObj && typeof consultantObj === 'object' && (consultantObj?.respondedAt || consultantObj?.at)) ? (consultantObj.respondedAt || consultantObj.at) : null;
                      const consName = (consultantObj && typeof consultantObj === 'object' && consultantObj?.author?.name) ? consultantObj.author.name : 'Byggkonsult';

                      const custThread = Array.isArray(val?.thread) ? val.thread : [];
                      const consThread = Array.isArray(consultantObj?.thread) ? consultantObj.thread : [];

                      // Bygg lista (exkludera borttagna kundkommentarer)
                      const custBase = (customerText && !isDeleted) ? [{ by: 'customer', text: customerText, at: customerAt, author: { name: customerName } }] : [];
                      const consBase = consText ? [{ by: 'consultant', text: consText, at: consAt, author: { name: consName } }] : [];

                      let messages = [];
                      if (custThread.length > 0 || consThread.length > 0) {
                        const filteredCustThread = isDeleted ? [] : custThread;
                        messages = [...filteredCustThread, ...consThread];
                      } else {
                        messages = [...custBase, ...consBase];
                      }

                      messages = messages.filter(Boolean).sort((a,b) => {
                        const ta = a?.at ? new Date(a.at).getTime() : 0;
                        const tb = b?.at ? new Date(b.at).getTime() : 0;
                        return ta - tb;
                      });

                      if (messages.length === 0) {
                        return <div className="text-sm text-gray-500 italic">Ingen kommentar</div>;
                      }

                      return (
                        <div className="mt-1 space-y-3">
                          {messages.map((t, i) => (
                            <div key={i}>
                              <div className="text-xs text-gray-500 flex items-center gap-2">
                                <span className="font-medium">{t?.author?.name || (t.by === 'consultant' ? 'Byggkonsult' : 'Du')}:</span>
                                {t.at && <span>{new Date(t.at).toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' })}</span>}
                              </div>
                              <div className={`text-sm mt-1 ${t.by === 'consultant' ? 'text-green-700' : 'text-gray-800'}`}>
                                {t.text}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                );
              } else {
                // SKRIVLÄGE: Visa kommentarsfält
                return (
                  <div className="flex flex-col gap-3">
                    <label className="text-sm font-medium text-gray-700">
                      {isCustomerView ? 'Din kommentar om:' : 'Anteckning om:'} Etablering, avfall & admin
                    </label>
                    <textarea
                      value={comments[eaCommentKey] || ''}
                      onChange={(e) => handleCommentChange(categoryKey, eaItemName, e.target.value)}
                      onBlur={() => {
                        if (isCustomerView) {
                          handleSaveComment(categoryKey, eaItemName);
                        } else {
                          handleSaveByggkonsultComment(categoryKey, eaItemName);
                        }
                      }}
                      placeholder={isCustomerView
                        ? "Skriv din kommentar eller fråga om denna punkt..."
                        : "Skriv en anteckning om denna punkt (t.ex. för omräkning)..."
                      }
                      className="w-full p-2 border border-gray-300 rounded-md text-sm"
                      rows="3"
                    />

                    {/* Ta bort-knapp (bara om kunden har en icke-borttagen kommentar) */}
                    {estimatData?.customerResponse?.comments?.[eaCommentKey] && !estimatData?.customerResponse?.comments?.[eaCommentKey]?.deleted && isCustomerView && (
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          onClick={() => handleDeleteComment(categoryKey, eaItemName)}
                          className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded"
                        >
                          Ta bort
                        </button>
                      </div>
                    )}
                  </div>
                );
              }
            })()}
          </td>
        </tr>
      )}
    </>
  );
};

export default EtableringRow;

